import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Check, Phone, Mail, MapPin } from "lucide-react";
import { useI18n } from "../i18n/I18nProvider";
import { API } from "../lib/api";

export default function Contact() {
  const { t, locale } = useI18n();
  const [form, setForm] = React.useState({ full_name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, locale });
      setDone(true);
      toast.success(t("contact_page.toast_success"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("contact_page.toast_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="py-24 sm:py-32 border-b border-zinc-200 relative overflow-hidden">
        <div className="absolute inset-0 technical-grid pointer-events-none" />
        <div className="container-oj relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">{t("contact_page.eyebrow")}</div>
            <h1 className="font-display mt-4 text-balance text-5xl sm:text-6xl font-black tracking-tighter leading-[0.95] text-oj-ink">
              {t("contact_page.h1")}
            </h1>
            <p className="mt-5 max-w-md text-lg text-zinc-600 leading-relaxed">
              {t("contact_page.intro")}
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              <li className="flex items-start gap-2 text-zinc-800">
                <Phone className="size-4 mt-0.5 text-oj-brand" />
                <a className="hover:underline" href="tel:+41325100620">+41 32 510 06 20</a>
              </li>
              <li className="flex items-start gap-2 text-zinc-800">
                <Mail className="size-4 mt-0.5 text-oj-brand" />
                <a className="hover:underline" href="mailto:support@ojavana.com">support@ojavana.com</a>
              </li>
              <li className="flex items-start gap-2 text-zinc-800">
                <MapPin className="size-4 mt-0.5 text-oj-brand" />
                {t("contact_page.address")}
              </li>
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-2xl bg-white border border-zinc-200 shadow-sm overflow-hidden">
              <div className="px-6 sm:px-8 py-4 border-b border-zinc-200 flex items-center justify-end">
                <div className="text-xs text-zinc-500">{t("contact_page.time_hint")}</div>
              </div>

              {done ? (
                <div data-testid="contact-success" className="p-10 sm:p-14 text-center">
                  <div className="mx-auto size-14 rounded-full bg-oj-kiosk text-white grid place-items-center">
                    <Check className="size-7" />
                  </div>
                  <h3 className="font-display mt-6 text-3xl font-black tracking-tighter text-oj-ink">{t("contact_page.success_title")}</h3>
                  <p className="mt-3 text-zinc-600 max-w-sm mx-auto">
                    {t("contact_page.success_body")}
                  </p>
                  <button
                    onClick={() => { setDone(false); setForm({ full_name: "", phone: "", email: "", message: "" }); }}
                    className="mt-6 text-sm font-semibold text-oj-ink underline underline-offset-4"
                    data-testid="contact-reset"
                  >
                    {t("contact_page.send_another")}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="contact-form">
                  <Field label={t("contact_page.f_full_name")} required>
                    <input
                      data-testid="contact-input-name"
                      required
                      value={form.full_name}
                      onChange={set("full_name")}
                      placeholder={t("contact_page.ph_full_name")}
                      className={inputClass}
                    />
                  </Field>
                  <Field label={t("contact_page.f_phone")} required>
                    <input
                      data-testid="contact-input-phone"
                      required
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder={t("contact_page.ph_phone")}
                      className={inputClass}
                    />
                  </Field>
                  <Field label={t("contact_page.f_email")} required className="sm:col-span-2">
                    <input
                      data-testid="contact-input-email"
                      required
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder={t("contact_page.ph_email")}
                      className={inputClass}
                    />
                  </Field>
                  <Field label={t("contact_page.f_message")} required className="sm:col-span-2">
                    <textarea
                      data-testid="contact-input-message"
                      required
                      rows={5}
                      value={form.message}
                      onChange={set("message")}
                      placeholder={t("contact_page.ph_message")}
                      className={inputClass + " resize-none"}
                    />
                  </Field>

                  <div className="sm:col-span-2 flex items-center justify-between gap-4 pt-2">
                    <div className="text-xs text-zinc-500">{t("contact_page.agree")}</div>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit"
                      className="inline-flex items-center gap-1.5 bg-oj-brand text-oj-deep font-semibold px-6 py-3.5 rounded-md hover:bg-[#6FBDB9] transition-colors disabled:opacity-60"
                    >
                      {submitting ? t("contact_page.sending") : t("contact_page.send")} <ArrowUpRight className="size-4" />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const inputClass = "w-full bg-zinc-50 border border-zinc-200 rounded-md px-4 py-3 text-sm text-oj-ink placeholder:text-zinc-400 focus:outline-none focus:border-oj-ink focus:ring-2 focus:ring-oj-ink/10 transition";

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-500">
        {label} {required && <span className="text-oj-pos">*</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
